/* eslint-disable */
import Long from "long";
import * as _m0 from "protobufjs/minimal";
import { TravelAttributes } from "./TravelAttributes";
import { AccommodationData } from "./AccommodationData";
import { PriceInfo } from "../Price/PriceInfo";
import { Availability } from "./Availability";
import { Voucher } from "./Voucher/Voucher";
import { BookingLink } from "./BookingLink";
import { DebugInformation } from "./DebugInfo";
import {
  FlightDirection,
  OvernightHint,
  FlightHint,
  StopoverHint,
  VisaConditions,
  overnightHintToNumber,
  flightHintToNumber,
  stopoverHintToNumber,
  overnightHintFromJSON,
  flightHintFromJSON,
  stopoverHintFromJSON,
  overnightHintToJSON,
  flightHintToJSON,
  stopoverHintToJSON,
} from "./Flight";
import {
  HasLuggage,
  LuggageInfo,
  hasLuggageToNumber,
  hasLuggageFromJSON,
  hasLuggageToJSON,
} from "./Luggage";
import {
  RemarkType,
  remarkTypeToNumber,
  remarkTypeFromJSON,
  remarkTypeToJSON,
} from "../TourOperator/RemarkType";
import {
  CancellationType,
  cancellationTypeToNumber,
  cancellationTypeFromJSON,
  cancellationTypeToJSON,
} from "./CancellationType";
import { TourOperatorRemark } from "../TourOperator/TourOperator";

export interface Points {
  /** the points amount */
  count: number;
  /** the money amount */
  value: number;
  /** the cryptedParam (for unified login) */
  cryptedParam: string;
}

export interface Offer {
  isEnteringTheBox: boolean;
  startedEnteringTheBox: boolean;
  didAdditionalSupplierRequests: boolean;
  hotelId: number;
  id: string;
  accommodationCode: string;
  /** @deprecated */
  tourOperatorAlias: string;
  tourOperatorCode: string;
  supplierCode: string;
  cryptString: string;
  detailsUrl: string;
  position: string;
  baseId: string;
  travelAttributes?: TravelAttributes;
  accommodationData?: AccommodationData;
  price?: PriceInfo;
  availability?: Availability;
  flight?: Offer_Flight;
  bargain?: Offer_Bargain;
  flags?: Offer_Flags;
  points?: Points;
  voucher?: Voucher;
  cancellation?: Offer_Cancellation;
  guide?: Offer_Guide;
  leafletData?: Offer_LeafletData;
  upsellPrediction?: Offer_UpsellPrediction;
  bookingLink?: BookingLink;
  tracking?: Offer_Tracking;
  boInfo?: Offer_BackofficeInformation;
  debugInfo?: DebugInformation;
  beo?: Offer_BestEffortOfferData;
  enhanced?: Offer_Enhancements;
  remarks: TourOperatorRemark[];
  possibleCancellations: Offer_PossibleCancellation[];
  badgeList: Offer_Badge[];
}

export interface Offer_Flight {
  /**
   * use overNightHint instead
   *
   * @deprecated
   */
  hasOvernightInfo: boolean;
  flightHash: string;
  outbound?: FlightDirection;
  inbound?: FlightDirection;
  hasLuggage: HasLuggage;
  luggageInfo?: LuggageInfo;
  overNightHint: OvernightHint;
  flightHint: FlightHint;
  stopoverHint: StopoverHint;
  visa?: VisaConditions;
}

export interface Offer_Cancellation {
  hasNoRemark: boolean;
  daysAfterBooking: number;
  validUntil: string;
}

export interface Offer_Bargain {
  text: string;
  tooltip: string;
}

export interface Offer_Flags {
  isExclusive: boolean;
  /** @deprecated */
  isLastMinute: boolean;
  /** @deprecated */
  isEarlyBird: boolean;
  isBestPrice: boolean;
  isSemOffer: boolean;
  exclusiveBannerDescription: string;
}

export interface Offer_Guide {
  label: string;
  description: string;
}

/**
 * fixme should just be called cancellations (repeated Offer.Cancellation cancellations = X;) where one entry can be selected @PRPOR-34006
 * cancellations and remarks should be two separate message types. cancellationFeeTitle does not belong into TourOperatorRemark
 * and RemarkType does not belong into Cancellation. In the frontend remarks are just chips displayed on the offer with a title
 * and a description. the type is only used to assign a color to the chip. we should not filter remarks in the frontend to find
 * cancellations.
 */
export interface Offer_PossibleCancellation {
  isBestFree: boolean;
  fee: number;
  touroperatorRemarkId: number;
  feeTitle: string;
  validUntil: string;
  touroperatorRemarkType: RemarkType;
  type: CancellationType;
}

export interface Offer_LeafletData {
  shareLink: string;
  leafletData: string;
  type: string;
}

export interface Offer_Tracking {
  bookEventLabelData: string;
  clickEvent: string;
  vacancyPrice: string;
  vacancyCheck: string;
}

export interface Offer_UpsellPrediction {
  hasAltFlights: boolean;
  hasUpselling: boolean;
  hasCancellationUpsell: boolean;
  hasLuggageUpsell: boolean;
  hasTransferUpsell: boolean;
}

export interface Offer_BackofficeInformation {
  offerType: string;
  bookingType: string;
  payment: string;
  supplier: string;
  travelTypeCode: string;
  hotelCode: string;
  roomCodes: string[];
}

export interface Offer_BestEffortOfferData {
  isMostPopularOffer: boolean;
  /** should only be true if the offer has no railAndFly but any other beo has it */
  forceNoRailAndFly: boolean;
  trackingId: number;
  type: string;
  groupType: string;
  label: string;
  tooltip: string;
  /** used to separate beo position from main list position, should only be filled if offer is in both lists */
  position: string;
  /** used to separate beo crypt string from main list crypt string, should only be filled if offer is in both lists */
  cryptString: string;
  /** used to separate beo tracking data from main list tracking data, should only be filled if offer is in both lists */
  tracking?: Offer_Tracking;
}

export interface Offer_Enhancements {
  roomType: boolean;
  mealType: boolean;
  privateTransfer: boolean;
  transfer: boolean;
  railAndFly: boolean;
  view: boolean;
  pool: boolean;
  exterior: boolean;
  oceanView: boolean;
  partialOceanView: boolean;
  dayDifference: number;
  luggage?: Offer_Enhancements_Luggage;
  cancellation?: Offer_Enhancements_Cancellation;
}

export interface Offer_Enhancements_Luggage {
  price: number;
  description: string;
  type: Offer_Enhancements_Luggage_Type;
}

export enum Offer_Enhancements_Luggage_Type {
  Luggage = "Luggage",
  AncillariesOne = "AncillariesOne",
  AncillariesAll = "AncillariesAll",
}

export function offer_Enhancements_Luggage_TypeFromJSON(
  object: any
): Offer_Enhancements_Luggage_Type {
  switch (object) {
    case 0:
    case "Luggage":
      return Offer_Enhancements_Luggage_Type.Luggage;
    case 1:
    case "AncillariesOne":
      return Offer_Enhancements_Luggage_Type.AncillariesOne;
    case 2:
    case "AncillariesAll":
      return Offer_Enhancements_Luggage_Type.AncillariesAll;
    default:
      throw new globalThis.Error(
        "Unrecognized enum value " +
          object +
          " for enum Offer_Enhancements_Luggage_Type"
      );
  }
}

export function offer_Enhancements_Luggage_TypeToJSON(
  object: Offer_Enhancements_Luggage_Type
): string {
  switch (object) {
    case Offer_Enhancements_Luggage_Type.Luggage:
      return "Luggage";
    case Offer_Enhancements_Luggage_Type.AncillariesOne:
      return "AncillariesOne";
    case Offer_Enhancements_Luggage_Type.AncillariesAll:
      return "AncillariesAll";
    default:
      return "UNKNOWN";
  }
}

export function offer_Enhancements_Luggage_TypeToNumber(
  object: Offer_Enhancements_Luggage_Type
): number {
  switch (object) {
    case Offer_Enhancements_Luggage_Type.Luggage:
      return 0;
    case Offer_Enhancements_Luggage_Type.AncillariesOne:
      return 1;
    case Offer_Enhancements_Luggage_Type.AncillariesAll:
      return 2;
    default:
      return 0;
  }
}

export interface Offer_Enhancements_Cancellation {
  option: boolean;
  cancellation: boolean;
  cancellationEconomic: boolean;
  rebookable: boolean;
}

export interface Offer_Badge {
  id: number;
  title: string;
  description: string;
  colorBadge: string;
  colorText: string;
  colorBorder: string;
  s4Active: boolean;
  s5Active: boolean;
}

function createBasePoints(): Points {
  return { count: 0, value: 0, cryptedParam: "" };
}

export const Points = {
  encode(
    message: Points,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.count !== 0) {
      writer.uint32(8).uint32(message.count);
    }
    if (message.value !== 0) {
      writer.uint32(21).float(message.value);
    }
    if (message.cryptedParam !== "") {
      writer.uint32(26).string(message.cryptedParam);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Points {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePoints();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.count = reader.uint32();
          break;
        case 2:
          message.value = reader.float();
          break;
        case 3:
          message.cryptedParam = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Points {
    return {
      count: isSet(object.count) ? Number(object.count) : 0,
      value: isSet(object.value) ? Number(object.value) : 0,
      cryptedParam: isSet(object.cryptedParam)
        ? String(object.cryptedParam)
        : "",
    };
  },

  toJSON(message: Points): unknown {
    const obj: any = {};
    message.count !== undefined && (obj.count = Math.round(message.count));
    message.value !== undefined && (obj.value = message.value);
    message.cryptedParam !== undefined &&
      (obj.cryptedParam = message.cryptedParam);
    return obj;
  },

  fromPartial(object: DeepPartial<Points>): Points {
    const message = createBasePoints();
    message.count = object.count ?? 0;
    message.value = object.value ?? 0;
    message.cryptedParam = object.cryptedParam ?? "";
    return message;
  },
};

function createBaseOffer(): Offer {
  return {
    isEnteringTheBox: false,
    startedEnteringTheBox: false,
    didAdditionalSupplierRequests: false,
    hotelId: 0,
    id: "",
    accommodationCode: "",
    tourOperatorAlias: "",
    tourOperatorCode: "",
    supplierCode: "",
    cryptString: "",
    detailsUrl: "",
    position: "",
    baseId: "",
    travelAttributes: undefined,
    accommodationData: undefined,
    price: undefined,
    availability: undefined,
    flight: undefined,
    bargain: undefined,
    flags: undefined,
    points: undefined,
    voucher: undefined,
    cancellation: undefined,
    guide: undefined,
    leafletData: undefined,
    upsellPrediction: undefined,
    bookingLink: undefined,
    tracking: undefined,
    boInfo: undefined,
    debugInfo: undefined,
    beo: undefined,
    enhanced: undefined,
    remarks: [],
    possibleCancellations: [],
    badgeList: [],
  };
}

export const Offer = {
  encode(message: Offer, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.isEnteringTheBox === true) {
      writer.uint32(272).bool(message.isEnteringTheBox);
    }
    if (message.startedEnteringTheBox === true) {
      writer.uint32(280).bool(message.startedEnteringTheBox);
    }
    if (message.didAdditionalSupplierRequests === true) {
      writer.uint32(288).bool(message.didAdditionalSupplierRequests);
    }
    if (message.hotelId !== 0) {
      writer.uint32(16).uint32(message.hotelId);
    }
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.accommodationCode !== "") {
      writer.uint32(258).string(message.accommodationCode);
    }
    if (message.tourOperatorAlias !== "") {
      writer.uint32(266).string(message.tourOperatorAlias);
    }
    if (message.tourOperatorCode !== "") {
      writer.uint32(26).string(message.tourOperatorCode);
    }
    if (message.supplierCode !== "") {
      writer.uint32(34).string(message.supplierCode);
    }
    if (message.cryptString !== "") {
      writer.uint32(42).string(message.cryptString);
    }
    if (message.detailsUrl !== "") {
      writer.uint32(154).string(message.detailsUrl);
    }
    if (message.position !== "") {
      writer.uint32(178).string(message.position);
    }
    if (message.baseId !== "") {
      writer.uint32(234).string(message.baseId);
    }
    if (message.travelAttributes !== undefined) {
      TravelAttributes.encode(
        message.travelAttributes,
        writer.uint32(50).fork()
      ).ldelim();
    }
    if (message.accommodationData !== undefined) {
      AccommodationData.encode(
        message.accommodationData,
        writer.uint32(58).fork()
      ).ldelim();
    }
    if (message.price !== undefined) {
      PriceInfo.encode(message.price, writer.uint32(66).fork()).ldelim();
    }
    if (message.availability !== undefined) {
      Availability.encode(
        message.availability,
        writer.uint32(74).fork()
      ).ldelim();
    }
    if (message.flight !== undefined) {
      Offer_Flight.encode(message.flight, writer.uint32(82).fork()).ldelim();
    }
    if (message.bargain !== undefined) {
      Offer_Bargain.encode(message.bargain, writer.uint32(90).fork()).ldelim();
    }
    if (message.flags !== undefined) {
      Offer_Flags.encode(message.flags, writer.uint32(106).fork()).ldelim();
    }
    if (message.points !== undefined) {
      Points.encode(message.points, writer.uint32(114).fork()).ldelim();
    }
    if (message.voucher !== undefined) {
      Voucher.encode(message.voucher, writer.uint32(122).fork()).ldelim();
    }
    if (message.cancellation !== undefined) {
      Offer_Cancellation.encode(
        message.cancellation,
        writer.uint32(130).fork()
      ).ldelim();
    }
    if (message.guide !== undefined) {
      Offer_Guide.encode(message.guide, writer.uint32(146).fork()).ldelim();
    }
    if (message.leafletData !== undefined) {
      Offer_LeafletData.encode(
        message.leafletData,
        writer.uint32(162).fork()
      ).ldelim();
    }
    if (message.upsellPrediction !== undefined) {
      Offer_UpsellPrediction.encode(
        message.upsellPrediction,
        writer.uint32(170).fork()
      ).ldelim();
    }
    if (message.bookingLink !== undefined) {
      BookingLink.encode(
        message.bookingLink,
        writer.uint32(186).fork()
      ).ldelim();
    }
    if (message.tracking !== undefined) {
      Offer_Tracking.encode(
        message.tracking,
        writer.uint32(194).fork()
      ).ldelim();
    }
    if (message.boInfo !== undefined) {
      Offer_BackofficeInformation.encode(
        message.boInfo,
        writer.uint32(202).fork()
      ).ldelim();
    }
    if (message.debugInfo !== undefined) {
      DebugInformation.encode(
        message.debugInfo,
        writer.uint32(210).fork()
      ).ldelim();
    }
    if (message.beo !== undefined) {
      Offer_BestEffortOfferData.encode(
        message.beo,
        writer.uint32(218).fork()
      ).ldelim();
    }
    if (message.enhanced !== undefined) {
      Offer_Enhancements.encode(
        message.enhanced,
        writer.uint32(226).fork()
      ).ldelim();
    }
    for (const v of message.remarks) {
      TourOperatorRemark.encode(v!, writer.uint32(98).fork()).ldelim();
    }
    for (const v of message.possibleCancellations) {
      Offer_PossibleCancellation.encode(v!, writer.uint32(138).fork()).ldelim();
    }
    for (const v of message.badgeList) {
      Offer_Badge.encode(v!, writer.uint32(242).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 34:
          message.isEnteringTheBox = reader.bool();
          break;
        case 35:
          message.startedEnteringTheBox = reader.bool();
          break;
        case 36:
          message.didAdditionalSupplierRequests = reader.bool();
          break;
        case 2:
          message.hotelId = reader.uint32();
          break;
        case 1:
          message.id = reader.string();
          break;
        case 32:
          message.accommodationCode = reader.string();
          break;
        case 33:
          message.tourOperatorAlias = reader.string();
          break;
        case 3:
          message.tourOperatorCode = reader.string();
          break;
        case 4:
          message.supplierCode = reader.string();
          break;
        case 5:
          message.cryptString = reader.string();
          break;
        case 19:
          message.detailsUrl = reader.string();
          break;
        case 22:
          message.position = reader.string();
          break;
        case 29:
          message.baseId = reader.string();
          break;
        case 6:
          message.travelAttributes = TravelAttributes.decode(
            reader,
            reader.uint32()
          );
          break;
        case 7:
          message.accommodationData = AccommodationData.decode(
            reader,
            reader.uint32()
          );
          break;
        case 8:
          message.price = PriceInfo.decode(reader, reader.uint32());
          break;
        case 9:
          message.availability = Availability.decode(reader, reader.uint32());
          break;
        case 10:
          message.flight = Offer_Flight.decode(reader, reader.uint32());
          break;
        case 11:
          message.bargain = Offer_Bargain.decode(reader, reader.uint32());
          break;
        case 13:
          message.flags = Offer_Flags.decode(reader, reader.uint32());
          break;
        case 14:
          message.points = Points.decode(reader, reader.uint32());
          break;
        case 15:
          message.voucher = Voucher.decode(reader, reader.uint32());
          break;
        case 16:
          message.cancellation = Offer_Cancellation.decode(
            reader,
            reader.uint32()
          );
          break;
        case 18:
          message.guide = Offer_Guide.decode(reader, reader.uint32());
          break;
        case 20:
          message.leafletData = Offer_LeafletData.decode(
            reader,
            reader.uint32()
          );
          break;
        case 21:
          message.upsellPrediction = Offer_UpsellPrediction.decode(
            reader,
            reader.uint32()
          );
          break;
        case 23:
          message.bookingLink = BookingLink.decode(reader, reader.uint32());
          break;
        case 24:
          message.tracking = Offer_Tracking.decode(reader, reader.uint32());
          break;
        case 25:
          message.boInfo = Offer_BackofficeInformation.decode(
            reader,
            reader.uint32()
          );
          break;
        case 26:
          message.debugInfo = DebugInformation.decode(reader, reader.uint32());
          break;
        case 27:
          message.beo = Offer_BestEffortOfferData.decode(
            reader,
            reader.uint32()
          );
          break;
        case 28:
          message.enhanced = Offer_Enhancements.decode(reader, reader.uint32());
          break;
        case 12:
          message.remarks.push(
            TourOperatorRemark.decode(reader, reader.uint32())
          );
          break;
        case 17:
          message.possibleCancellations.push(
            Offer_PossibleCancellation.decode(reader, reader.uint32())
          );
          break;
        case 30:
          message.badgeList.push(Offer_Badge.decode(reader, reader.uint32()));
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer {
    return {
      isEnteringTheBox: isSet(object.isEnteringTheBox)
        ? Boolean(object.isEnteringTheBox)
        : false,
      startedEnteringTheBox: isSet(object.startedEnteringTheBox)
        ? Boolean(object.startedEnteringTheBox)
        : false,
      didAdditionalSupplierRequests: isSet(object.didAdditionalSupplierRequests)
        ? Boolean(object.didAdditionalSupplierRequests)
        : false,
      hotelId: isSet(object.hotelId) ? Number(object.hotelId) : 0,
      id: isSet(object.id) ? String(object.id) : "",
      accommodationCode: isSet(object.accommodationCode)
        ? String(object.accommodationCode)
        : "",
      tourOperatorAlias: isSet(object.tourOperatorAlias)
        ? String(object.tourOperatorAlias)
        : "",
      tourOperatorCode: isSet(object.tourOperatorCode)
        ? String(object.tourOperatorCode)
        : "",
      supplierCode: isSet(object.supplierCode)
        ? String(object.supplierCode)
        : "",
      cryptString: isSet(object.cryptString) ? String(object.cryptString) : "",
      detailsUrl: isSet(object.detailsUrl) ? String(object.detailsUrl) : "",
      position: isSet(object.position) ? String(object.position) : "",
      baseId: isSet(object.baseId) ? String(object.baseId) : "",
      travelAttributes: isSet(object.travelAttributes)
        ? TravelAttributes.fromJSON(object.travelAttributes)
        : undefined,
      accommodationData: isSet(object.accommodationData)
        ? AccommodationData.fromJSON(object.accommodationData)
        : undefined,
      price: isSet(object.price) ? PriceInfo.fromJSON(object.price) : undefined,
      availability: isSet(object.availability)
        ? Availability.fromJSON(object.availability)
        : undefined,
      flight: isSet(object.flight)
        ? Offer_Flight.fromJSON(object.flight)
        : undefined,
      bargain: isSet(object.bargain)
        ? Offer_Bargain.fromJSON(object.bargain)
        : undefined,
      flags: isSet(object.flags)
        ? Offer_Flags.fromJSON(object.flags)
        : undefined,
      points: isSet(object.points) ? Points.fromJSON(object.points) : undefined,
      voucher: isSet(object.voucher)
        ? Voucher.fromJSON(object.voucher)
        : undefined,
      cancellation: isSet(object.cancellation)
        ? Offer_Cancellation.fromJSON(object.cancellation)
        : undefined,
      guide: isSet(object.guide)
        ? Offer_Guide.fromJSON(object.guide)
        : undefined,
      leafletData: isSet(object.leafletData)
        ? Offer_LeafletData.fromJSON(object.leafletData)
        : undefined,
      upsellPrediction: isSet(object.upsellPrediction)
        ? Offer_UpsellPrediction.fromJSON(object.upsellPrediction)
        : undefined,
      bookingLink: isSet(object.bookingLink)
        ? BookingLink.fromJSON(object.bookingLink)
        : undefined,
      tracking: isSet(object.tracking)
        ? Offer_Tracking.fromJSON(object.tracking)
        : undefined,
      boInfo: isSet(object.boInfo)
        ? Offer_BackofficeInformation.fromJSON(object.boInfo)
        : undefined,
      debugInfo: isSet(object.debugInfo)
        ? DebugInformation.fromJSON(object.debugInfo)
        : undefined,
      beo: isSet(object.beo)
        ? Offer_BestEffortOfferData.fromJSON(object.beo)
        : undefined,
      enhanced: isSet(object.enhanced)
        ? Offer_Enhancements.fromJSON(object.enhanced)
        : undefined,
      remarks: Array.isArray(object?.remarks)
        ? object.remarks.map((e: any) => TourOperatorRemark.fromJSON(e))
        : [],
      possibleCancellations: Array.isArray(object?.possibleCancellations)
        ? object.possibleCancellations.map((e: any) =>
            Offer_PossibleCancellation.fromJSON(e)
          )
        : [],
      badgeList: Array.isArray(object?.badgeList)
        ? object.badgeList.map((e: any) => Offer_Badge.fromJSON(e))
        : [],
    };
  },

  toJSON(message: Offer): unknown {
    const obj: any = {};
    message.isEnteringTheBox !== undefined &&
      (obj.isEnteringTheBox = message.isEnteringTheBox);
    message.startedEnteringTheBox !== undefined &&
      (obj.startedEnteringTheBox = message.startedEnteringTheBox);
    message.didAdditionalSupplierRequests !== undefined &&
      (obj.didAdditionalSupplierRequests =
        message.didAdditionalSupplierRequests);
    message.hotelId !== undefined &&
      (obj.hotelId = Math.round(message.hotelId));
    message.id !== undefined && (obj.id = message.id);
    message.accommodationCode !== undefined &&
      (obj.accommodationCode = message.accommodationCode);
    message.tourOperatorAlias !== undefined &&
      (obj.tourOperatorAlias = message.tourOperatorAlias);
    message.tourOperatorCode !== undefined &&
      (obj.tourOperatorCode = message.tourOperatorCode);
    message.supplierCode !== undefined &&
      (obj.supplierCode = message.supplierCode);
    message.cryptString !== undefined &&
      (obj.cryptString = message.cryptString);
    message.detailsUrl !== undefined && (obj.detailsUrl = message.detailsUrl);
    message.position !== undefined && (obj.position = message.position);
    message.baseId !== undefined && (obj.baseId = message.baseId);
    message.travelAttributes !== undefined &&
      (obj.travelAttributes = message.travelAttributes
        ? TravelAttributes.toJSON(message.travelAttributes)
        : undefined);
    message.accommodationData !== undefined &&
      (obj.accommodationData = message.accommodationData
        ? AccommodationData.toJSON(message.accommodationData)
        : undefined);
    message.price !== undefined &&
      (obj.price = message.price ? PriceInfo.toJSON(message.price) : undefined);
    message.availability !== undefined &&
      (obj.availability = message.availability
        ? Availability.toJSON(message.availability)
        : undefined);
    message.flight !== undefined &&
      (obj.flight = message.flight
        ? Offer_Flight.toJSON(message.flight)
        : undefined);
    message.bargain !== undefined &&
      (obj.bargain = message.bargain
        ? Offer_Bargain.toJSON(message.bargain)
        : undefined);
    message.flags !== undefined &&
      (obj.flags = message.flags
        ? Offer_Flags.toJSON(message.flags)
        : undefined);
    message.points !== undefined &&
      (obj.points = message.points ? Points.toJSON(message.points) : undefined);
    message.voucher !== undefined &&
      (obj.voucher = message.voucher
        ? Voucher.toJSON(message.voucher)
        : undefined);
    message.cancellation !== undefined &&
      (obj.cancellation = message.cancellation
        ? Offer_Cancellation.toJSON(message.cancellation)
        : undefined);
    message.guide !== undefined &&
      (obj.guide = message.guide
        ? Offer_Guide.toJSON(message.guide)
        : undefined);
    message.leafletData !== undefined &&
      (obj.leafletData = message.leafletData
        ? Offer_LeafletData.toJSON(message.leafletData)
        : undefined);
    message.upsellPrediction !== undefined &&
      (obj.upsellPrediction = message.upsellPrediction
        ? Offer_UpsellPrediction.toJSON(message.upsellPrediction)
        : undefined);
    message.bookingLink !== undefined &&
      (obj.bookingLink = message.bookingLink
        ? BookingLink.toJSON(message.bookingLink)
        : undefined);
    message.tracking !== undefined &&
      (obj.tracking = message.tracking
        ? Offer_Tracking.toJSON(message.tracking)
        : undefined);
    message.boInfo !== undefined &&
      (obj.boInfo = message.boInfo
        ? Offer_BackofficeInformation.toJSON(message.boInfo)
        : undefined);
    message.debugInfo !== undefined &&
      (obj.debugInfo = message.debugInfo
        ? DebugInformation.toJSON(message.debugInfo)
        : undefined);
    message.beo !== undefined &&
      (obj.beo = message.beo
        ? Offer_BestEffortOfferData.toJSON(message.beo)
        : undefined);
    message.enhanced !== undefined &&
      (obj.enhanced = message.enhanced
        ? Offer_Enhancements.toJSON(message.enhanced)
        : undefined);
    if (message.remarks) {
      obj.remarks = message.remarks.map((e) =>
        e ? TourOperatorRemark.toJSON(e) : undefined
      );
    } else {
      obj.remarks = [];
    }
    if (message.possibleCancellations) {
      obj.possibleCancellations = message.possibleCancellations.map((e) =>
        e ? Offer_PossibleCancellation.toJSON(e) : undefined
      );
    } else {
      obj.possibleCancellations = [];
    }
    if (message.badgeList) {
      obj.badgeList = message.badgeList.map((e) =>
        e ? Offer_Badge.toJSON(e) : undefined
      );
    } else {
      obj.badgeList = [];
    }
    return obj;
  },

  fromPartial(object: DeepPartial<Offer>): Offer {
    const message = createBaseOffer();
    message.isEnteringTheBox = object.isEnteringTheBox ?? false;
    message.startedEnteringTheBox = object.startedEnteringTheBox ?? false;
    message.didAdditionalSupplierRequests =
      object.didAdditionalSupplierRequests ?? false;
    message.hotelId = object.hotelId ?? 0;
    message.id = object.id ?? "";
    message.accommodationCode = object.accommodationCode ?? "";
    message.tourOperatorAlias = object.tourOperatorAlias ?? "";
    message.tourOperatorCode = object.tourOperatorCode ?? "";
    message.supplierCode = object.supplierCode ?? "";
    message.cryptString = object.cryptString ?? "";
    message.detailsUrl = object.detailsUrl ?? "";
    message.position = object.position ?? "";
    message.baseId = object.baseId ?? "";
    message.travelAttributes =
      object.travelAttributes !== undefined && object.travelAttributes !== null
        ? TravelAttributes.fromPartial(object.travelAttributes)
        : undefined;
    message.accommodationData =
      object.accommodationData !== undefined &&
      object.accommodationData !== null
        ? AccommodationData.fromPartial(object.accommodationData)
        : undefined;
    message.price =
      object.price !== undefined && object.price !== null
        ? PriceInfo.fromPartial(object.price)
        : undefined;
    message.availability =
      object.availability !== undefined && object.availability !== null
        ? Availability.fromPartial(object.availability)
        : undefined;
    message.flight =
      object.flight !== undefined && object.flight !== null
        ? Offer_Flight.fromPartial(object.flight)
        : undefined;
    message.bargain =
      object.bargain !== undefined && object.bargain !== null
        ? Offer_Bargain.fromPartial(object.bargain)
        : undefined;
    message.flags =
      object.flags !== undefined && object.flags !== null
        ? Offer_Flags.fromPartial(object.flags)
        : undefined;
    message.points =
      object.points !== undefined && object.points !== null
        ? Points.fromPartial(object.points)
        : undefined;
    message.voucher =
      object.voucher !== undefined && object.voucher !== null
        ? Voucher.fromPartial(object.voucher)
        : undefined;
    message.cancellation =
      object.cancellation !== undefined && object.cancellation !== null
        ? Offer_Cancellation.fromPartial(object.cancellation)
        : undefined;
    message.guide =
      object.guide !== undefined && object.guide !== null
        ? Offer_Guide.fromPartial(object.guide)
        : undefined;
    message.leafletData =
      object.leafletData !== undefined && object.leafletData !== null
        ? Offer_LeafletData.fromPartial(object.leafletData)
        : undefined;
    message.upsellPrediction =
      object.upsellPrediction !== undefined && object.upsellPrediction !== null
        ? Offer_UpsellPrediction.fromPartial(object.upsellPrediction)
        : undefined;
    message.bookingLink =
      object.bookingLink !== undefined && object.bookingLink !== null
        ? BookingLink.fromPartial(object.bookingLink)
        : undefined;
    message.tracking =
      object.tracking !== undefined && object.tracking !== null
        ? Offer_Tracking.fromPartial(object.tracking)
        : undefined;
    message.boInfo =
      object.boInfo !== undefined && object.boInfo !== null
        ? Offer_BackofficeInformation.fromPartial(object.boInfo)
        : undefined;
    message.debugInfo =
      object.debugInfo !== undefined && object.debugInfo !== null
        ? DebugInformation.fromPartial(object.debugInfo)
        : undefined;
    message.beo =
      object.beo !== undefined && object.beo !== null
        ? Offer_BestEffortOfferData.fromPartial(object.beo)
        : undefined;
    message.enhanced =
      object.enhanced !== undefined && object.enhanced !== null
        ? Offer_Enhancements.fromPartial(object.enhanced)
        : undefined;
    message.remarks =
      object.remarks?.map((e) => TourOperatorRemark.fromPartial(e)) || [];
    message.possibleCancellations =
      object.possibleCancellations?.map((e) =>
        Offer_PossibleCancellation.fromPartial(e)
      ) || [];
    message.badgeList =
      object.badgeList?.map((e) => Offer_Badge.fromPartial(e)) || [];
    return message;
  },
};

function createBaseOffer_Flight(): Offer_Flight {
  return {
    hasOvernightInfo: false,
    flightHash: "",
    outbound: undefined,
    inbound: undefined,
    hasLuggage: HasLuggage.LuggageUnknown,
    luggageInfo: undefined,
    overNightHint: OvernightHint.NoOverNightHint,
    flightHint: FlightHint.NoFlightHint,
    stopoverHint: StopoverHint.NoStopOverHint,
    visa: undefined,
  };
}

export const Offer_Flight = {
  encode(
    message: Offer_Flight,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.hasOvernightInfo === true) {
      writer.uint32(40).bool(message.hasOvernightInfo);
    }
    if (message.flightHash !== "") {
      writer.uint32(74).string(message.flightHash);
    }
    if (message.outbound !== undefined) {
      FlightDirection.encode(
        message.outbound,
        writer.uint32(10).fork()
      ).ldelim();
    }
    if (message.inbound !== undefined) {
      FlightDirection.encode(
        message.inbound,
        writer.uint32(18).fork()
      ).ldelim();
    }
    if (message.hasLuggage !== HasLuggage.LuggageUnknown) {
      writer.uint32(24).int32(hasLuggageToNumber(message.hasLuggage));
    }
    if (message.luggageInfo !== undefined) {
      LuggageInfo.encode(
        message.luggageInfo,
        writer.uint32(34).fork()
      ).ldelim();
    }
    if (message.overNightHint !== OvernightHint.NoOverNightHint) {
      writer.uint32(48).int32(overnightHintToNumber(message.overNightHint));
    }
    if (message.flightHint !== FlightHint.NoFlightHint) {
      writer.uint32(56).int32(flightHintToNumber(message.flightHint));
    }
    if (message.stopoverHint !== StopoverHint.NoStopOverHint) {
      writer.uint32(64).int32(stopoverHintToNumber(message.stopoverHint));
    }
    if (message.visa !== undefined) {
      VisaConditions.encode(message.visa, writer.uint32(82).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Flight {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Flight();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 5:
          message.hasOvernightInfo = reader.bool();
          break;
        case 9:
          message.flightHash = reader.string();
          break;
        case 1:
          message.outbound = FlightDirection.decode(reader, reader.uint32());
          break;
        case 2:
          message.inbound = FlightDirection.decode(reader, reader.uint32());
          break;
        case 3:
          message.hasLuggage = hasLuggageFromJSON(reader.int32());
          break;
        case 4:
          message.luggageInfo = LuggageInfo.decode(reader, reader.uint32());
          break;
        case 6:
          message.overNightHint = overnightHintFromJSON(reader.int32());
          break;
        case 7:
          message.flightHint = flightHintFromJSON(reader.int32());
          break;
        case 8:
          message.stopoverHint = stopoverHintFromJSON(reader.int32());
          break;
        case 10:
          message.visa = VisaConditions.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Flight {
    return {
      hasOvernightInfo: isSet(object.hasOvernightInfo)
        ? Boolean(object.hasOvernightInfo)
        : false,
      flightHash: isSet(object.flightHash) ? String(object.flightHash) : "",
      outbound: isSet(object.outbound)
        ? FlightDirection.fromJSON(object.outbound)
        : undefined,
      inbound: isSet(object.inbound)
        ? FlightDirection.fromJSON(object.inbound)
        : undefined,
      hasLuggage: isSet(object.hasLuggage)
        ? hasLuggageFromJSON(object.hasLuggage)
        : HasLuggage.LuggageUnknown,
      luggageInfo: isSet(object.luggageInfo)
        ? LuggageInfo.fromJSON(object.luggageInfo)
        : undefined,
      overNightHint: isSet(object.overNightHint)
        ? overnightHintFromJSON(object.overNightHint)
        : OvernightHint.NoOverNightHint,
      flightHint: isSet(object.flightHint)
        ? flightHintFromJSON(object.flightHint)
        : FlightHint.NoFlightHint,
      stopoverHint: isSet(object.stopoverHint)
        ? stopoverHintFromJSON(object.stopoverHint)
        : StopoverHint.NoStopOverHint,
      visa: isSet(object.visa)
        ? VisaConditions.fromJSON(object.visa)
        : undefined,
    };
  },

  toJSON(message: Offer_Flight): unknown {
    const obj: any = {};
    message.hasOvernightInfo !== undefined &&
      (obj.hasOvernightInfo = message.hasOvernightInfo);
    message.flightHash !== undefined && (obj.flightHash = message.flightHash);
    message.outbound !== undefined &&
      (obj.outbound = message.outbound
        ? FlightDirection.toJSON(message.outbound)
        : undefined);
    message.inbound !== undefined &&
      (obj.inbound = message.inbound
        ? FlightDirection.toJSON(message.inbound)
        : undefined);
    message.hasLuggage !== undefined &&
      (obj.hasLuggage = hasLuggageToJSON(message.hasLuggage));
    message.luggageInfo !== undefined &&
      (obj.luggageInfo = message.luggageInfo
        ? LuggageInfo.toJSON(message.luggageInfo)
        : undefined);
    message.overNightHint !== undefined &&
      (obj.overNightHint = overnightHintToJSON(message.overNightHint));
    message.flightHint !== undefined &&
      (obj.flightHint = flightHintToJSON(message.flightHint));
    message.stopoverHint !== undefined &&
      (obj.stopoverHint = stopoverHintToJSON(message.stopoverHint));
    message.visa !== undefined &&
      (obj.visa = message.visa
        ? VisaConditions.toJSON(message.visa)
        : undefined);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Flight>): Offer_Flight {
    const message = createBaseOffer_Flight();
    message.hasOvernightInfo = object.hasOvernightInfo ?? false;
    message.flightHash = object.flightHash ?? "";
    message.outbound =
      object.outbound !== undefined && object.outbound !== null
        ? FlightDirection.fromPartial(object.outbound)
        : undefined;
    message.inbound =
      object.inbound !== undefined && object.inbound !== null
        ? FlightDirection.fromPartial(object.inbound)
        : undefined;
    message.hasLuggage = object.hasLuggage ?? HasLuggage.LuggageUnknown;
    message.luggageInfo =
      object.luggageInfo !== undefined && object.luggageInfo !== null
        ? LuggageInfo.fromPartial(object.luggageInfo)
        : undefined;
    message.overNightHint =
      object.overNightHint ?? OvernightHint.NoOverNightHint;
    message.flightHint = object.flightHint ?? FlightHint.NoFlightHint;
    message.stopoverHint = object.stopoverHint ?? StopoverHint.NoStopOverHint;
    message.visa =
      object.visa !== undefined && object.visa !== null
        ? VisaConditions.fromPartial(object.visa)
        : undefined;
    return message;
  },
};

function createBaseOffer_Cancellation(): Offer_Cancellation {
  return { hasNoRemark: false, daysAfterBooking: 0, validUntil: "" };
}

export const Offer_Cancellation = {
  encode(
    message: Offer_Cancellation,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.hasNoRemark === true) {
      writer.uint32(24).bool(message.hasNoRemark);
    }
    if (message.daysAfterBooking !== 0) {
      writer.uint32(16).uint32(message.daysAfterBooking);
    }
    if (message.validUntil !== "") {
      writer.uint32(10).string(message.validUntil);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Cancellation {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Cancellation();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 3:
          message.hasNoRemark = reader.bool();
          break;
        case 2:
          message.daysAfterBooking = reader.uint32();
          break;
        case 1:
          message.validUntil = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Cancellation {
    return {
      hasNoRemark: isSet(object.hasNoRemark)
        ? Boolean(object.hasNoRemark)
        : false,
      daysAfterBooking: isSet(object.daysAfterBooking)
        ? Number(object.daysAfterBooking)
        : 0,
      validUntil: isSet(object.validUntil) ? String(object.validUntil) : "",
    };
  },

  toJSON(message: Offer_Cancellation): unknown {
    const obj: any = {};
    message.hasNoRemark !== undefined &&
      (obj.hasNoRemark = message.hasNoRemark);
    message.daysAfterBooking !== undefined &&
      (obj.daysAfterBooking = Math.round(message.daysAfterBooking));
    message.validUntil !== undefined && (obj.validUntil = message.validUntil);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Cancellation>): Offer_Cancellation {
    const message = createBaseOffer_Cancellation();
    message.hasNoRemark = object.hasNoRemark ?? false;
    message.daysAfterBooking = object.daysAfterBooking ?? 0;
    message.validUntil = object.validUntil ?? "";
    return message;
  },
};

function createBaseOffer_Bargain(): Offer_Bargain {
  return { text: "", tooltip: "" };
}

export const Offer_Bargain = {
  encode(
    message: Offer_Bargain,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.text !== "") {
      writer.uint32(10).string(message.text);
    }
    if (message.tooltip !== "") {
      writer.uint32(18).string(message.tooltip);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Bargain {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Bargain();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.text = reader.string();
          break;
        case 2:
          message.tooltip = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Bargain {
    return {
      text: isSet(object.text) ? String(object.text) : "",
      tooltip: isSet(object.tooltip) ? String(object.tooltip) : "",
    };
  },

  toJSON(message: Offer_Bargain): unknown {
    const obj: any = {};
    message.text !== undefined && (obj.text = message.text);
    message.tooltip !== undefined && (obj.tooltip = message.tooltip);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Bargain>): Offer_Bargain {
    const message = createBaseOffer_Bargain();
    message.text = object.text ?? "";
    message.tooltip = object.tooltip ?? "";
    return message;
  },
};

function createBaseOffer_Flags(): Offer_Flags {
  return {
    isExclusive: false,
    isLastMinute: false,
    isEarlyBird: false,
    isBestPrice: false,
    isSemOffer: false,
    exclusiveBannerDescription: "",
  };
}

export const Offer_Flags = {
  encode(
    message: Offer_Flags,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.isExclusive === true) {
      writer.uint32(8).bool(message.isExclusive);
    }
    if (message.isLastMinute === true) {
      writer.uint32(16).bool(message.isLastMinute);
    }
    if (message.isEarlyBird === true) {
      writer.uint32(24).bool(message.isEarlyBird);
    }
    if (message.isBestPrice === true) {
      writer.uint32(40).bool(message.isBestPrice);
    }
    if (message.isSemOffer === true) {
      writer.uint32(48).bool(message.isSemOffer);
    }
    if (message.exclusiveBannerDescription !== "") {
      writer.uint32(34).string(message.exclusiveBannerDescription);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Flags {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Flags();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.isExclusive = reader.bool();
          break;
        case 2:
          message.isLastMinute = reader.bool();
          break;
        case 3:
          message.isEarlyBird = reader.bool();
          break;
        case 5:
          message.isBestPrice = reader.bool();
          break;
        case 6:
          message.isSemOffer = reader.bool();
          break;
        case 4:
          message.exclusiveBannerDescription = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Flags {
    return {
      isExclusive: isSet(object.isExclusive)
        ? Boolean(object.isExclusive)
        : false,
      isLastMinute: isSet(object.isLastMinute)
        ? Boolean(object.isLastMinute)
        : false,
      isEarlyBird: isSet(object.isEarlyBird)
        ? Boolean(object.isEarlyBird)
        : false,
      isBestPrice: isSet(object.isBestPrice)
        ? Boolean(object.isBestPrice)
        : false,
      isSemOffer: isSet(object.isSemOffer) ? Boolean(object.isSemOffer) : false,
      exclusiveBannerDescription: isSet(object.exclusiveBannerDescription)
        ? String(object.exclusiveBannerDescription)
        : "",
    };
  },

  toJSON(message: Offer_Flags): unknown {
    const obj: any = {};
    message.isExclusive !== undefined &&
      (obj.isExclusive = message.isExclusive);
    message.isLastMinute !== undefined &&
      (obj.isLastMinute = message.isLastMinute);
    message.isEarlyBird !== undefined &&
      (obj.isEarlyBird = message.isEarlyBird);
    message.isBestPrice !== undefined &&
      (obj.isBestPrice = message.isBestPrice);
    message.isSemOffer !== undefined && (obj.isSemOffer = message.isSemOffer);
    message.exclusiveBannerDescription !== undefined &&
      (obj.exclusiveBannerDescription = message.exclusiveBannerDescription);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Flags>): Offer_Flags {
    const message = createBaseOffer_Flags();
    message.isExclusive = object.isExclusive ?? false;
    message.isLastMinute = object.isLastMinute ?? false;
    message.isEarlyBird = object.isEarlyBird ?? false;
    message.isBestPrice = object.isBestPrice ?? false;
    message.isSemOffer = object.isSemOffer ?? false;
    message.exclusiveBannerDescription =
      object.exclusiveBannerDescription ?? "";
    return message;
  },
};

function createBaseOffer_Guide(): Offer_Guide {
  return { label: "", description: "" };
}

export const Offer_Guide = {
  encode(
    message: Offer_Guide,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.label !== "") {
      writer.uint32(10).string(message.label);
    }
    if (message.description !== "") {
      writer.uint32(18).string(message.description);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Guide {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Guide();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.label = reader.string();
          break;
        case 2:
          message.description = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Guide {
    return {
      label: isSet(object.label) ? String(object.label) : "",
      description: isSet(object.description) ? String(object.description) : "",
    };
  },

  toJSON(message: Offer_Guide): unknown {
    const obj: any = {};
    message.label !== undefined && (obj.label = message.label);
    message.description !== undefined &&
      (obj.description = message.description);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Guide>): Offer_Guide {
    const message = createBaseOffer_Guide();
    message.label = object.label ?? "";
    message.description = object.description ?? "";
    return message;
  },
};

function createBaseOffer_PossibleCancellation(): Offer_PossibleCancellation {
  return {
    isBestFree: false,
    fee: 0,
    touroperatorRemarkId: 0,
    feeTitle: "",
    validUntil: "",
    touroperatorRemarkType: RemarkType.TypeUnknown,
    type: CancellationType.TypeUnknown,
  };
}

export const Offer_PossibleCancellation = {
  encode(
    message: Offer_PossibleCancellation,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.isBestFree === true) {
      writer.uint32(56).bool(message.isBestFree);
    }
    if (message.fee !== 0) {
      writer.uint32(13).float(message.fee);
    }
    if (message.touroperatorRemarkId !== 0) {
      writer.uint32(16).int32(message.touroperatorRemarkId);
    }
    if (message.feeTitle !== "") {
      writer.uint32(26).string(message.feeTitle);
    }
    if (message.validUntil !== "") {
      writer.uint32(42).string(message.validUntil);
    }
    if (message.touroperatorRemarkType !== RemarkType.TypeUnknown) {
      writer
        .uint32(32)
        .int32(remarkTypeToNumber(message.touroperatorRemarkType));
    }
    if (message.type !== CancellationType.TypeUnknown) {
      writer.uint32(48).int32(cancellationTypeToNumber(message.type));
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Offer_PossibleCancellation {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_PossibleCancellation();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 7:
          message.isBestFree = reader.bool();
          break;
        case 1:
          message.fee = reader.float();
          break;
        case 2:
          message.touroperatorRemarkId = reader.int32();
          break;
        case 3:
          message.feeTitle = reader.string();
          break;
        case 5:
          message.validUntil = reader.string();
          break;
        case 4:
          message.touroperatorRemarkType = remarkTypeFromJSON(reader.int32());
          break;
        case 6:
          message.type = cancellationTypeFromJSON(reader.int32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_PossibleCancellation {
    return {
      isBestFree: isSet(object.isBestFree) ? Boolean(object.isBestFree) : false,
      fee: isSet(object.fee) ? Number(object.fee) : 0,
      touroperatorRemarkId: isSet(object.touroperatorRemarkId)
        ? Number(object.touroperatorRemarkId)
        : 0,
      feeTitle: isSet(object.feeTitle) ? String(object.feeTitle) : "",
      validUntil: isSet(object.validUntil) ? String(object.validUntil) : "",
      touroperatorRemarkType: isSet(object.touroperatorRemarkType)
        ? remarkTypeFromJSON(object.touroperatorRemarkType)
        : RemarkType.TypeUnknown,
      type: isSet(object.type)
        ? cancellationTypeFromJSON(object.type)
        : CancellationType.TypeUnknown,
    };
  },

  toJSON(message: Offer_PossibleCancellation): unknown {
    const obj: any = {};
    message.isBestFree !== undefined && (obj.isBestFree = message.isBestFree);
    message.fee !== undefined && (obj.fee = message.fee);
    message.touroperatorRemarkId !== undefined &&
      (obj.touroperatorRemarkId = Math.round(message.touroperatorRemarkId));
    message.feeTitle !== undefined && (obj.feeTitle = message.feeTitle);
    message.validUntil !== undefined && (obj.validUntil = message.validUntil);
    message.touroperatorRemarkType !== undefined &&
      (obj.touroperatorRemarkType = remarkTypeToJSON(
        message.touroperatorRemarkType
      ));
    message.type !== undefined &&
      (obj.type = cancellationTypeToJSON(message.type));
    return obj;
  },

  fromPartial(
    object: DeepPartial<Offer_PossibleCancellation>
  ): Offer_PossibleCancellation {
    const message = createBaseOffer_PossibleCancellation();
    message.isBestFree = object.isBestFree ?? false;
    message.fee = object.fee ?? 0;
    message.touroperatorRemarkId = object.touroperatorRemarkId ?? 0;
    message.feeTitle = object.feeTitle ?? "";
    message.validUntil = object.validUntil ?? "";
    message.touroperatorRemarkType =
      object.touroperatorRemarkType ?? RemarkType.TypeUnknown;
    message.type = object.type ?? CancellationType.TypeUnknown;
    return message;
  },
};

function createBaseOffer_LeafletData(): Offer_LeafletData {
  return { shareLink: "", leafletData: "", type: "" };
}

export const Offer_LeafletData = {
  encode(
    message: Offer_LeafletData,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.shareLink !== "") {
      writer.uint32(10).string(message.shareLink);
    }
    if (message.leafletData !== "") {
      writer.uint32(18).string(message.leafletData);
    }
    if (message.type !== "") {
      writer.uint32(26).string(message.type);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_LeafletData {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_LeafletData();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.shareLink = reader.string();
          break;
        case 2:
          message.leafletData = reader.string();
          break;
        case 3:
          message.type = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_LeafletData {
    return {
      shareLink: isSet(object.shareLink) ? String(object.shareLink) : "",
      leafletData: isSet(object.leafletData) ? String(object.leafletData) : "",
      type: isSet(object.type) ? String(object.type) : "",
    };
  },

  toJSON(message: Offer_LeafletData): unknown {
    const obj: any = {};
    message.shareLink !== undefined && (obj.shareLink = message.shareLink);
    message.leafletData !== undefined &&
      (obj.leafletData = message.leafletData);
    message.type !== undefined && (obj.type = message.type);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_LeafletData>): Offer_LeafletData {
    const message = createBaseOffer_LeafletData();
    message.shareLink = object.shareLink ?? "";
    message.leafletData = object.leafletData ?? "";
    message.type = object.type ?? "";
    return message;
  },
};

function createBaseOffer_Tracking(): Offer_Tracking {
  return {
    bookEventLabelData: "",
    clickEvent: "",
    vacancyPrice: "",
    vacancyCheck: "",
  };
}

export const Offer_Tracking = {
  encode(
    message: Offer_Tracking,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.bookEventLabelData !== "") {
      writer.uint32(10).string(message.bookEventLabelData);
    }
    if (message.clickEvent !== "") {
      writer.uint32(18).string(message.clickEvent);
    }
    if (message.vacancyPrice !== "") {
      writer.uint32(26).string(message.vacancyPrice);
    }
    if (message.vacancyCheck !== "") {
      writer.uint32(34).string(message.vacancyCheck);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Tracking {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Tracking();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.bookEventLabelData = reader.string();
          break;
        case 2:
          message.clickEvent = reader.string();
          break;
        case 3:
          message.vacancyPrice = reader.string();
          break;
        case 4:
          message.vacancyCheck = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Tracking {
    return {
      bookEventLabelData: isSet(object.bookEventLabelData)
        ? String(object.bookEventLabelData)
        : "",
      clickEvent: isSet(object.clickEvent) ? String(object.clickEvent) : "",
      vacancyPrice: isSet(object.vacancyPrice)
        ? String(object.vacancyPrice)
        : "",
      vacancyCheck: isSet(object.vacancyCheck)
        ? String(object.vacancyCheck)
        : "",
    };
  },

  toJSON(message: Offer_Tracking): unknown {
    const obj: any = {};
    message.bookEventLabelData !== undefined &&
      (obj.bookEventLabelData = message.bookEventLabelData);
    message.clickEvent !== undefined && (obj.clickEvent = message.clickEvent);
    message.vacancyPrice !== undefined &&
      (obj.vacancyPrice = message.vacancyPrice);
    message.vacancyCheck !== undefined &&
      (obj.vacancyCheck = message.vacancyCheck);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Tracking>): Offer_Tracking {
    const message = createBaseOffer_Tracking();
    message.bookEventLabelData = object.bookEventLabelData ?? "";
    message.clickEvent = object.clickEvent ?? "";
    message.vacancyPrice = object.vacancyPrice ?? "";
    message.vacancyCheck = object.vacancyCheck ?? "";
    return message;
  },
};

function createBaseOffer_UpsellPrediction(): Offer_UpsellPrediction {
  return {
    hasAltFlights: false,
    hasUpselling: false,
    hasCancellationUpsell: false,
    hasLuggageUpsell: false,
    hasTransferUpsell: false,
  };
}

export const Offer_UpsellPrediction = {
  encode(
    message: Offer_UpsellPrediction,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.hasAltFlights === true) {
      writer.uint32(8).bool(message.hasAltFlights);
    }
    if (message.hasUpselling === true) {
      writer.uint32(16).bool(message.hasUpselling);
    }
    if (message.hasCancellationUpsell === true) {
      writer.uint32(24).bool(message.hasCancellationUpsell);
    }
    if (message.hasLuggageUpsell === true) {
      writer.uint32(32).bool(message.hasLuggageUpsell);
    }
    if (message.hasTransferUpsell === true) {
      writer.uint32(40).bool(message.hasTransferUpsell);
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Offer_UpsellPrediction {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_UpsellPrediction();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.hasAltFlights = reader.bool();
          break;
        case 2:
          message.hasUpselling = reader.bool();
          break;
        case 3:
          message.hasCancellationUpsell = reader.bool();
          break;
        case 4:
          message.hasLuggageUpsell = reader.bool();
          break;
        case 5:
          message.hasTransferUpsell = reader.bool();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_UpsellPrediction {
    return {
      hasAltFlights: isSet(object.hasAltFlights)
        ? Boolean(object.hasAltFlights)
        : false,
      hasUpselling: isSet(object.hasUpselling)
        ? Boolean(object.hasUpselling)
        : false,
      hasCancellationUpsell: isSet(object.hasCancellationUpsell)
        ? Boolean(object.hasCancellationUpsell)
        : false,
      hasLuggageUpsell: isSet(object.hasLuggageUpsell)
        ? Boolean(object.hasLuggageUpsell)
        : false,
      hasTransferUpsell: isSet(object.hasTransferUpsell)
        ? Boolean(object.hasTransferUpsell)
        : false,
    };
  },

  toJSON(message: Offer_UpsellPrediction): unknown {
    const obj: any = {};
    message.hasAltFlights !== undefined &&
      (obj.hasAltFlights = message.hasAltFlights);
    message.hasUpselling !== undefined &&
      (obj.hasUpselling = message.hasUpselling);
    message.hasCancellationUpsell !== undefined &&
      (obj.hasCancellationUpsell = message.hasCancellationUpsell);
    message.hasLuggageUpsell !== undefined &&
      (obj.hasLuggageUpsell = message.hasLuggageUpsell);
    message.hasTransferUpsell !== undefined &&
      (obj.hasTransferUpsell = message.hasTransferUpsell);
    return obj;
  },

  fromPartial(
    object: DeepPartial<Offer_UpsellPrediction>
  ): Offer_UpsellPrediction {
    const message = createBaseOffer_UpsellPrediction();
    message.hasAltFlights = object.hasAltFlights ?? false;
    message.hasUpselling = object.hasUpselling ?? false;
    message.hasCancellationUpsell = object.hasCancellationUpsell ?? false;
    message.hasLuggageUpsell = object.hasLuggageUpsell ?? false;
    message.hasTransferUpsell = object.hasTransferUpsell ?? false;
    return message;
  },
};

function createBaseOffer_BackofficeInformation(): Offer_BackofficeInformation {
  return {
    offerType: "",
    bookingType: "",
    payment: "",
    supplier: "",
    travelTypeCode: "",
    hotelCode: "",
    roomCodes: [],
  };
}

export const Offer_BackofficeInformation = {
  encode(
    message: Offer_BackofficeInformation,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.offerType !== "") {
      writer.uint32(10).string(message.offerType);
    }
    if (message.bookingType !== "") {
      writer.uint32(18).string(message.bookingType);
    }
    if (message.payment !== "") {
      writer.uint32(26).string(message.payment);
    }
    if (message.supplier !== "") {
      writer.uint32(34).string(message.supplier);
    }
    if (message.travelTypeCode !== "") {
      writer.uint32(42).string(message.travelTypeCode);
    }
    if (message.hotelCode !== "") {
      writer.uint32(50).string(message.hotelCode);
    }
    for (const v of message.roomCodes) {
      writer.uint32(58).string(v!);
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Offer_BackofficeInformation {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_BackofficeInformation();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.offerType = reader.string();
          break;
        case 2:
          message.bookingType = reader.string();
          break;
        case 3:
          message.payment = reader.string();
          break;
        case 4:
          message.supplier = reader.string();
          break;
        case 5:
          message.travelTypeCode = reader.string();
          break;
        case 6:
          message.hotelCode = reader.string();
          break;
        case 7:
          message.roomCodes.push(reader.string());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_BackofficeInformation {
    return {
      offerType: isSet(object.offerType) ? String(object.offerType) : "",
      bookingType: isSet(object.bookingType) ? String(object.bookingType) : "",
      payment: isSet(object.payment) ? String(object.payment) : "",
      supplier: isSet(object.supplier) ? String(object.supplier) : "",
      travelTypeCode: isSet(object.travelTypeCode)
        ? String(object.travelTypeCode)
        : "",
      hotelCode: isSet(object.hotelCode) ? String(object.hotelCode) : "",
      roomCodes: Array.isArray(object?.roomCodes)
        ? object.roomCodes.map((e: any) => String(e))
        : [],
    };
  },

  toJSON(message: Offer_BackofficeInformation): unknown {
    const obj: any = {};
    message.offerType !== undefined && (obj.offerType = message.offerType);
    message.bookingType !== undefined &&
      (obj.bookingType = message.bookingType);
    message.payment !== undefined && (obj.payment = message.payment);
    message.supplier !== undefined && (obj.supplier = message.supplier);
    message.travelTypeCode !== undefined &&
      (obj.travelTypeCode = message.travelTypeCode);
    message.hotelCode !== undefined && (obj.hotelCode = message.hotelCode);
    if (message.roomCodes) {
      obj.roomCodes = message.roomCodes.map((e) => e);
    } else {
      obj.roomCodes = [];
    }
    return obj;
  },

  fromPartial(
    object: DeepPartial<Offer_BackofficeInformation>
  ): Offer_BackofficeInformation {
    const message = createBaseOffer_BackofficeInformation();
    message.offerType = object.offerType ?? "";
    message.bookingType = object.bookingType ?? "";
    message.payment = object.payment ?? "";
    message.supplier = object.supplier ?? "";
    message.travelTypeCode = object.travelTypeCode ?? "";
    message.hotelCode = object.hotelCode ?? "";
    message.roomCodes = object.roomCodes?.map((e) => e) || [];
    return message;
  },
};

function createBaseOffer_BestEffortOfferData(): Offer_BestEffortOfferData {
  return {
    isMostPopularOffer: false,
    forceNoRailAndFly: false,
    trackingId: 0,
    type: "",
    groupType: "",
    label: "",
    tooltip: "",
    position: "",
    cryptString: "",
    tracking: undefined,
  };
}

export const Offer_BestEffortOfferData = {
  encode(
    message: Offer_BestEffortOfferData,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.isMostPopularOffer === true) {
      writer.uint32(16).bool(message.isMostPopularOffer);
    }
    if (message.forceNoRailAndFly === true) {
      writer.uint32(56).bool(message.forceNoRailAndFly);
    }
    if (message.trackingId !== 0) {
      writer.uint32(24).uint32(message.trackingId);
    }
    if (message.type !== "") {
      writer.uint32(10).string(message.type);
    }
    if (message.groupType !== "") {
      writer.uint32(34).string(message.groupType);
    }
    if (message.label !== "") {
      writer.uint32(42).string(message.label);
    }
    if (message.tooltip !== "") {
      writer.uint32(50).string(message.tooltip);
    }
    if (message.position !== "") {
      writer.uint32(74).string(message.position);
    }
    if (message.cryptString !== "") {
      writer.uint32(82).string(message.cryptString);
    }
    if (message.tracking !== undefined) {
      Offer_Tracking.encode(
        message.tracking,
        writer.uint32(66).fork()
      ).ldelim();
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Offer_BestEffortOfferData {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_BestEffortOfferData();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          message.isMostPopularOffer = reader.bool();
          break;
        case 7:
          message.forceNoRailAndFly = reader.bool();
          break;
        case 3:
          message.trackingId = reader.uint32();
          break;
        case 1:
          message.type = reader.string();
          break;
        case 4:
          message.groupType = reader.string();
          break;
        case 5:
          message.label = reader.string();
          break;
        case 6:
          message.tooltip = reader.string();
          break;
        case 9:
          message.position = reader.string();
          break;
        case 10:
          message.cryptString = reader.string();
          break;
        case 8:
          message.tracking = Offer_Tracking.decode(reader, reader.uint32());
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_BestEffortOfferData {
    return {
      isMostPopularOffer: isSet(object.isMostPopularOffer)
        ? Boolean(object.isMostPopularOffer)
        : false,
      forceNoRailAndFly: isSet(object.forceNoRailAndFly)
        ? Boolean(object.forceNoRailAndFly)
        : false,
      trackingId: isSet(object.trackingId) ? Number(object.trackingId) : 0,
      type: isSet(object.type) ? String(object.type) : "",
      groupType: isSet(object.groupType) ? String(object.groupType) : "",
      label: isSet(object.label) ? String(object.label) : "",
      tooltip: isSet(object.tooltip) ? String(object.tooltip) : "",
      position: isSet(object.position) ? String(object.position) : "",
      cryptString: isSet(object.cryptString) ? String(object.cryptString) : "",
      tracking: isSet(object.tracking)
        ? Offer_Tracking.fromJSON(object.tracking)
        : undefined,
    };
  },

  toJSON(message: Offer_BestEffortOfferData): unknown {
    const obj: any = {};
    message.isMostPopularOffer !== undefined &&
      (obj.isMostPopularOffer = message.isMostPopularOffer);
    message.forceNoRailAndFly !== undefined &&
      (obj.forceNoRailAndFly = message.forceNoRailAndFly);
    message.trackingId !== undefined &&
      (obj.trackingId = Math.round(message.trackingId));
    message.type !== undefined && (obj.type = message.type);
    message.groupType !== undefined && (obj.groupType = message.groupType);
    message.label !== undefined && (obj.label = message.label);
    message.tooltip !== undefined && (obj.tooltip = message.tooltip);
    message.position !== undefined && (obj.position = message.position);
    message.cryptString !== undefined &&
      (obj.cryptString = message.cryptString);
    message.tracking !== undefined &&
      (obj.tracking = message.tracking
        ? Offer_Tracking.toJSON(message.tracking)
        : undefined);
    return obj;
  },

  fromPartial(
    object: DeepPartial<Offer_BestEffortOfferData>
  ): Offer_BestEffortOfferData {
    const message = createBaseOffer_BestEffortOfferData();
    message.isMostPopularOffer = object.isMostPopularOffer ?? false;
    message.forceNoRailAndFly = object.forceNoRailAndFly ?? false;
    message.trackingId = object.trackingId ?? 0;
    message.type = object.type ?? "";
    message.groupType = object.groupType ?? "";
    message.label = object.label ?? "";
    message.tooltip = object.tooltip ?? "";
    message.position = object.position ?? "";
    message.cryptString = object.cryptString ?? "";
    message.tracking =
      object.tracking !== undefined && object.tracking !== null
        ? Offer_Tracking.fromPartial(object.tracking)
        : undefined;
    return message;
  },
};

function createBaseOffer_Enhancements(): Offer_Enhancements {
  return {
    roomType: false,
    mealType: false,
    privateTransfer: false,
    transfer: false,
    railAndFly: false,
    view: false,
    pool: false,
    exterior: false,
    oceanView: false,
    partialOceanView: false,
    dayDifference: 0,
    luggage: undefined,
    cancellation: undefined,
  };
}

export const Offer_Enhancements = {
  encode(
    message: Offer_Enhancements,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.roomType === true) {
      writer.uint32(8).bool(message.roomType);
    }
    if (message.mealType === true) {
      writer.uint32(16).bool(message.mealType);
    }
    if (message.privateTransfer === true) {
      writer.uint32(24).bool(message.privateTransfer);
    }
    if (message.transfer === true) {
      writer.uint32(32).bool(message.transfer);
    }
    if (message.railAndFly === true) {
      writer.uint32(40).bool(message.railAndFly);
    }
    if (message.view === true) {
      writer.uint32(48).bool(message.view);
    }
    if (message.pool === true) {
      writer.uint32(56).bool(message.pool);
    }
    if (message.exterior === true) {
      writer.uint32(64).bool(message.exterior);
    }
    if (message.oceanView === true) {
      writer.uint32(72).bool(message.oceanView);
    }
    if (message.partialOceanView === true) {
      writer.uint32(80).bool(message.partialOceanView);
    }
    if (message.dayDifference !== 0) {
      writer.uint32(88).sint32(message.dayDifference);
    }
    if (message.luggage !== undefined) {
      Offer_Enhancements_Luggage.encode(
        message.luggage,
        writer.uint32(98).fork()
      ).ldelim();
    }
    if (message.cancellation !== undefined) {
      Offer_Enhancements_Cancellation.encode(
        message.cancellation,
        writer.uint32(106).fork()
      ).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Enhancements {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Enhancements();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.roomType = reader.bool();
          break;
        case 2:
          message.mealType = reader.bool();
          break;
        case 3:
          message.privateTransfer = reader.bool();
          break;
        case 4:
          message.transfer = reader.bool();
          break;
        case 5:
          message.railAndFly = reader.bool();
          break;
        case 6:
          message.view = reader.bool();
          break;
        case 7:
          message.pool = reader.bool();
          break;
        case 8:
          message.exterior = reader.bool();
          break;
        case 9:
          message.oceanView = reader.bool();
          break;
        case 10:
          message.partialOceanView = reader.bool();
          break;
        case 11:
          message.dayDifference = reader.sint32();
          break;
        case 12:
          message.luggage = Offer_Enhancements_Luggage.decode(
            reader,
            reader.uint32()
          );
          break;
        case 13:
          message.cancellation = Offer_Enhancements_Cancellation.decode(
            reader,
            reader.uint32()
          );
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Enhancements {
    return {
      roomType: isSet(object.roomType) ? Boolean(object.roomType) : false,
      mealType: isSet(object.mealType) ? Boolean(object.mealType) : false,
      privateTransfer: isSet(object.privateTransfer)
        ? Boolean(object.privateTransfer)
        : false,
      transfer: isSet(object.transfer) ? Boolean(object.transfer) : false,
      railAndFly: isSet(object.railAndFly) ? Boolean(object.railAndFly) : false,
      view: isSet(object.view) ? Boolean(object.view) : false,
      pool: isSet(object.pool) ? Boolean(object.pool) : false,
      exterior: isSet(object.exterior) ? Boolean(object.exterior) : false,
      oceanView: isSet(object.oceanView) ? Boolean(object.oceanView) : false,
      partialOceanView: isSet(object.partialOceanView)
        ? Boolean(object.partialOceanView)
        : false,
      dayDifference: isSet(object.dayDifference)
        ? Number(object.dayDifference)
        : 0,
      luggage: isSet(object.luggage)
        ? Offer_Enhancements_Luggage.fromJSON(object.luggage)
        : undefined,
      cancellation: isSet(object.cancellation)
        ? Offer_Enhancements_Cancellation.fromJSON(object.cancellation)
        : undefined,
    };
  },

  toJSON(message: Offer_Enhancements): unknown {
    const obj: any = {};
    message.roomType !== undefined && (obj.roomType = message.roomType);
    message.mealType !== undefined && (obj.mealType = message.mealType);
    message.privateTransfer !== undefined &&
      (obj.privateTransfer = message.privateTransfer);
    message.transfer !== undefined && (obj.transfer = message.transfer);
    message.railAndFly !== undefined && (obj.railAndFly = message.railAndFly);
    message.view !== undefined && (obj.view = message.view);
    message.pool !== undefined && (obj.pool = message.pool);
    message.exterior !== undefined && (obj.exterior = message.exterior);
    message.oceanView !== undefined && (obj.oceanView = message.oceanView);
    message.partialOceanView !== undefined &&
      (obj.partialOceanView = message.partialOceanView);
    message.dayDifference !== undefined &&
      (obj.dayDifference = Math.round(message.dayDifference));
    message.luggage !== undefined &&
      (obj.luggage = message.luggage
        ? Offer_Enhancements_Luggage.toJSON(message.luggage)
        : undefined);
    message.cancellation !== undefined &&
      (obj.cancellation = message.cancellation
        ? Offer_Enhancements_Cancellation.toJSON(message.cancellation)
        : undefined);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Enhancements>): Offer_Enhancements {
    const message = createBaseOffer_Enhancements();
    message.roomType = object.roomType ?? false;
    message.mealType = object.mealType ?? false;
    message.privateTransfer = object.privateTransfer ?? false;
    message.transfer = object.transfer ?? false;
    message.railAndFly = object.railAndFly ?? false;
    message.view = object.view ?? false;
    message.pool = object.pool ?? false;
    message.exterior = object.exterior ?? false;
    message.oceanView = object.oceanView ?? false;
    message.partialOceanView = object.partialOceanView ?? false;
    message.dayDifference = object.dayDifference ?? 0;
    message.luggage =
      object.luggage !== undefined && object.luggage !== null
        ? Offer_Enhancements_Luggage.fromPartial(object.luggage)
        : undefined;
    message.cancellation =
      object.cancellation !== undefined && object.cancellation !== null
        ? Offer_Enhancements_Cancellation.fromPartial(object.cancellation)
        : undefined;
    return message;
  },
};

function createBaseOffer_Enhancements_Luggage(): Offer_Enhancements_Luggage {
  return {
    price: 0,
    description: "",
    type: Offer_Enhancements_Luggage_Type.Luggage,
  };
}

export const Offer_Enhancements_Luggage = {
  encode(
    message: Offer_Enhancements_Luggage,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.price !== 0) {
      writer.uint32(29).float(message.price);
    }
    if (message.description !== "") {
      writer.uint32(18).string(message.description);
    }
    if (message.type !== Offer_Enhancements_Luggage_Type.Luggage) {
      writer
        .uint32(8)
        .int32(offer_Enhancements_Luggage_TypeToNumber(message.type));
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Offer_Enhancements_Luggage {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Enhancements_Luggage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 3:
          message.price = reader.float();
          break;
        case 2:
          message.description = reader.string();
          break;
        case 1:
          message.type = offer_Enhancements_Luggage_TypeFromJSON(
            reader.int32()
          );
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Enhancements_Luggage {
    return {
      price: isSet(object.price) ? Number(object.price) : 0,
      description: isSet(object.description) ? String(object.description) : "",
      type: isSet(object.type)
        ? offer_Enhancements_Luggage_TypeFromJSON(object.type)
        : Offer_Enhancements_Luggage_Type.Luggage,
    };
  },

  toJSON(message: Offer_Enhancements_Luggage): unknown {
    const obj: any = {};
    message.price !== undefined && (obj.price = message.price);
    message.description !== undefined &&
      (obj.description = message.description);
    message.type !== undefined &&
      (obj.type = offer_Enhancements_Luggage_TypeToJSON(message.type));
    return obj;
  },

  fromPartial(
    object: DeepPartial<Offer_Enhancements_Luggage>
  ): Offer_Enhancements_Luggage {
    const message = createBaseOffer_Enhancements_Luggage();
    message.price = object.price ?? 0;
    message.description = object.description ?? "";
    message.type = object.type ?? Offer_Enhancements_Luggage_Type.Luggage;
    return message;
  },
};

function createBaseOffer_Enhancements_Cancellation(): Offer_Enhancements_Cancellation {
  return {
    option: false,
    cancellation: false,
    cancellationEconomic: false,
    rebookable: false,
  };
}

export const Offer_Enhancements_Cancellation = {
  encode(
    message: Offer_Enhancements_Cancellation,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.option === true) {
      writer.uint32(8).bool(message.option);
    }
    if (message.cancellation === true) {
      writer.uint32(16).bool(message.cancellation);
    }
    if (message.cancellationEconomic === true) {
      writer.uint32(24).bool(message.cancellationEconomic);
    }
    if (message.rebookable === true) {
      writer.uint32(32).bool(message.rebookable);
    }
    return writer;
  },

  decode(
    input: _m0.Reader | Uint8Array,
    length?: number
  ): Offer_Enhancements_Cancellation {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Enhancements_Cancellation();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.option = reader.bool();
          break;
        case 2:
          message.cancellation = reader.bool();
          break;
        case 3:
          message.cancellationEconomic = reader.bool();
          break;
        case 4:
          message.rebookable = reader.bool();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Enhancements_Cancellation {
    return {
      option: isSet(object.option) ? Boolean(object.option) : false,
      cancellation: isSet(object.cancellation)
        ? Boolean(object.cancellation)
        : false,
      cancellationEconomic: isSet(object.cancellationEconomic)
        ? Boolean(object.cancellationEconomic)
        : false,
      rebookable: isSet(object.rebookable) ? Boolean(object.rebookable) : false,
    };
  },

  toJSON(message: Offer_Enhancements_Cancellation): unknown {
    const obj: any = {};
    message.option !== undefined && (obj.option = message.option);
    message.cancellation !== undefined &&
      (obj.cancellation = message.cancellation);
    message.cancellationEconomic !== undefined &&
      (obj.cancellationEconomic = message.cancellationEconomic);
    message.rebookable !== undefined && (obj.rebookable = message.rebookable);
    return obj;
  },

  fromPartial(
    object: DeepPartial<Offer_Enhancements_Cancellation>
  ): Offer_Enhancements_Cancellation {
    const message = createBaseOffer_Enhancements_Cancellation();
    message.option = object.option ?? false;
    message.cancellation = object.cancellation ?? false;
    message.cancellationEconomic = object.cancellationEconomic ?? false;
    message.rebookable = object.rebookable ?? false;
    return message;
  },
};

function createBaseOffer_Badge(): Offer_Badge {
  return {
    id: 0,
    title: "",
    description: "",
    colorBadge: "",
    colorText: "",
    colorBorder: "",
    s4Active: false,
    s5Active: false,
  };
}

export const Offer_Badge = {
  encode(
    message: Offer_Badge,
    writer: _m0.Writer = _m0.Writer.create()
  ): _m0.Writer {
    if (message.id !== 0) {
      writer.uint32(8).uint32(message.id);
    }
    if (message.title !== "") {
      writer.uint32(18).string(message.title);
    }
    if (message.description !== "") {
      writer.uint32(74).string(message.description);
    }
    if (message.colorBadge !== "") {
      writer.uint32(26).string(message.colorBadge);
    }
    if (message.colorText !== "") {
      writer.uint32(34).string(message.colorText);
    }
    if (message.colorBorder !== "") {
      writer.uint32(42).string(message.colorBorder);
    }
    if (message.s4Active === true) {
      writer.uint32(48).bool(message.s4Active);
    }
    if (message.s5Active === true) {
      writer.uint32(56).bool(message.s5Active);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Offer_Badge {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseOffer_Badge();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.id = reader.uint32();
          break;
        case 2:
          message.title = reader.string();
          break;
        case 9:
          message.description = reader.string();
          break;
        case 3:
          message.colorBadge = reader.string();
          break;
        case 4:
          message.colorText = reader.string();
          break;
        case 5:
          message.colorBorder = reader.string();
          break;
        case 6:
          message.s4Active = reader.bool();
          break;
        case 7:
          message.s5Active = reader.bool();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Offer_Badge {
    return {
      id: isSet(object.id) ? Number(object.id) : 0,
      title: isSet(object.title) ? String(object.title) : "",
      description: isSet(object.description) ? String(object.description) : "",
      colorBadge: isSet(object.colorBadge) ? String(object.colorBadge) : "",
      colorText: isSet(object.colorText) ? String(object.colorText) : "",
      colorBorder: isSet(object.colorBorder) ? String(object.colorBorder) : "",
      s4Active: isSet(object.s4Active) ? Boolean(object.s4Active) : false,
      s5Active: isSet(object.s5Active) ? Boolean(object.s5Active) : false,
    };
  },

  toJSON(message: Offer_Badge): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = Math.round(message.id));
    message.title !== undefined && (obj.title = message.title);
    message.description !== undefined &&
      (obj.description = message.description);
    message.colorBadge !== undefined && (obj.colorBadge = message.colorBadge);
    message.colorText !== undefined && (obj.colorText = message.colorText);
    message.colorBorder !== undefined &&
      (obj.colorBorder = message.colorBorder);
    message.s4Active !== undefined && (obj.s4Active = message.s4Active);
    message.s5Active !== undefined && (obj.s5Active = message.s5Active);
    return obj;
  },

  fromPartial(object: DeepPartial<Offer_Badge>): Offer_Badge {
    const message = createBaseOffer_Badge();
    message.id = object.id ?? 0;
    message.title = object.title ?? "";
    message.description = object.description ?? "";
    message.colorBadge = object.colorBadge ?? "";
    message.colorText = object.colorText ?? "";
    message.colorBorder = object.colorBorder ?? "";
    message.s4Active = object.s4Active ?? false;
    message.s5Active = object.s5Active ?? false;
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var globalThis: any = (() => {
  if (typeof globalThis !== "undefined") return globalThis;
  if (typeof self !== "undefined") return self;
  if (typeof window !== "undefined") return window;
  if (typeof global !== "undefined") return global;
  throw "Unable to locate global object";
})();

type Builtin =
  | Date
  | Function
  | Uint8Array
  | string
  | number
  | boolean
  | undefined;

type DeepPartial<T> = T extends Builtin
  ? T
  : T extends Array<infer U>
  ? Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U>
  ? ReadonlyArray<DeepPartial<U>>
  : T extends {}
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
